import React, { useEffect, useState } from "react";
import { Container, Spinner, Alert } from "reactstrap";
import MetaTags from "react-meta-tags";
import { get } from "helpers/api_helper";
import EmployeeHeaderCard from "./components/EmployeeHeaderCard";
import EmployeeStatsWidgets from "./components/EmployeeStatsWidgets";
import EmployeeNotifications from "./components/EmployeeNotifications";
import EmployeeRecentEntries from "./components/EmployeeRecentEntries";
import OrderForm from "modules/Orders/form";
import RequestEditModal from "modules/Approvals/components/RequestEditModal";
import { ORDER_URL } from "helpers/url_helper";

const EmployeeDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dashboardData, setDashboardData] = useState(null);

    // Order Drawer State
    const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isEditData, setIsEditData] = useState(false);
    const [isViewOnly, setIsViewOnly] = useState(false);
    const [approvalToken, setApprovalToken] = useState(null);
    const [approvalStatus, setApprovalStatus] = useState(null);

    // Request Edit Modal State
    const [isRequestEditModalOpen, setIsRequestEditModalOpen] = useState(false);
    const [requestEditDoc, setRequestEditDoc] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await get("/api/employee/dashboard");
                if (isMounted) {
                    if (res.status === 200) {
                        setDashboardData(res.response);
                    } else {
                        setError(res.message || "Failed to load employee dashboard data.");
                    }
                }
            } catch (err) {
                if (isMounted) {
                    setError(err?.message || "Failed to connect to dashboard service.");
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            isMounted = false;
        };
    }, []);

    const fetchDashboardData = async () => {
        try {
            const res = await get("/api/employee/dashboard");
            if (res.status === 200) {
                setDashboardData(res.response);
            }
        } catch (e) {
            console.error("Dashboard refresh error:", e);
        }
    };

    // Handle opening an order from dashboard
    const handleViewOrder = async (orderItem) => {
        try {
            const orderKey = typeof orderItem === "object"
                ? (orderItem?.order_number || orderItem?.document_number || orderItem?.document_id || orderItem?.id)
                : orderItem;

            if (!orderKey) return;

            const statusRes = await get(`/api/approvals/check_status?document_type=order&document_id=${encodeURIComponent(orderKey)}`);
            const canEdit = statusRes?.response?.can_edit;
            const token = statusRes?.response?.token;
            const status = statusRes?.response?.status;

            setIsViewOnly(!canEdit);
            setApprovalToken(token);
            setApprovalStatus(status);
            setRequestEditDoc(typeof orderItem === "object" ? orderItem : { id: orderKey, order_number: orderKey, document_type: "order" });

            const res = await get(`${ORDER_URL}?order=${encodeURIComponent(orderKey)}`);
            if (res && res.status === 200 && res.response && typeof res.response === 'object') {
                setSelectedOrder(res.response);
                setIsEditData(true);
                setIsOrderDrawerOpen(true);
            }
        } catch (e) {
            console.error("Error opening order:", e);
        }
    };

    // Handle EDIT action: if has approval/admin -> open edit form, else -> open request edit modal
    const handleEditOrder = async (orderItem) => {
        try {
            const orderKey = typeof orderItem === "object"
                ? (orderItem?.order_number || orderItem?.document_number || orderItem?.document_id || orderItem?.id)
                : orderItem;

            if (!orderKey) return;

            const statusRes = await get(`/api/approvals/check_status?document_type=order&document_id=${encodeURIComponent(orderKey)}`);
            const canEdit = statusRes?.response?.can_edit;
            const token = statusRes?.response?.token;
            const status = statusRes?.response?.status;

            const docPayload = typeof orderItem === "object"
                ? {
                    ...orderItem,
                    id: orderItem.id || orderKey,
                    order_number: orderItem.order_number || orderItem.document_number || orderKey,
                    document_id: orderItem.document_id || orderItem.id || orderKey,
                    document_number: orderItem.document_number || orderItem.order_number || orderKey,
                    document_type: "order"
                }
                : { id: orderKey, order_number: orderKey, document_id: orderKey, document_number: orderKey, document_type: "order" };

            setRequestEditDoc(docPayload);

            if (canEdit) {
                // Has approval or is admin: open editable form
                setIsViewOnly(false);
                setApprovalToken(token);
                setApprovalStatus(status);

                const res = await get(`${ORDER_URL}?order=${encodeURIComponent(orderKey)}`);
                if (res && res.status === 200 && res.response && typeof res.response === 'object') {
                    setSelectedOrder(res.response);
                    setIsEditData(true);
                    setIsOrderDrawerOpen(true);
                }
            } else {
                // Does not have approval: open Request Edit Authorization modal directly
                setApprovalStatus(status);
                setIsRequestEditModalOpen(true);
            }
        } catch (e) {
            console.error("Error on edit order:", e);
        }
    };

    // Handle New Order Trigger
    const handleNewOrder = () => {
        setSelectedOrder(null);
        setIsEditData(false);
        setIsViewOnly(false);
        setApprovalToken(null);
        setApprovalStatus(null);
        setIsOrderDrawerOpen(true);
    };

    return (
        <React.Fragment>
            <div className="page-content p-t-2">
                <MetaTags>
                    <title>Oralstop | Employee Dashboard</title>
                </MetaTags>

                <Container fluid>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
                            <h5 className="mt-3 text-muted">Loading your workspace...</h5>
                        </div>
                    ) : error ? (
                        <Alert color="danger" className="my-4">
                            <i className="mdi mdi-alert-circle me-2"></i>
                            {error}
                        </Alert>
                    ) : (
                        <>
                            {/* Employee Welcome & Attendance Header */}
                            <EmployeeHeaderCard
                                employee={dashboardData?.employee}
                                attendance={dashboardData?.attendance}
                                onNewOrderClick={handleNewOrder}
                                onRequestEditClick={() => {
                                    setRequestEditDoc(null);
                                    setIsRequestEditModalOpen(true);
                                }}
                            />

                            {/* Notifications & Action Center */}
                            <EmployeeNotifications
                                approvedTokens={dashboardData?.approved_tokens}
                                pendingCount={dashboardData?.stats?.pending_approvals}
                                dueTodayCount={dashboardData?.stats?.due_today}
                                onOpenOrder={handleEditOrder}
                            />

                            {/* Key Stats Widgets */}
                            <EmployeeStatsWidgets stats={dashboardData?.stats} />

                            {/* Recent Entries & Activity Tables */}
                            <EmployeeRecentEntries
                                recentOrders={dashboardData?.recent_orders}
                                recentProcess={dashboardData?.recent_process}
                                recentApprovals={dashboardData?.recent_approvals}
                                onViewOrder={handleViewOrder}
                                onEditOrder={handleEditOrder}
                            />
                        </>
                    )}
                </Container>
            </div>

            {/* New / View Order Drawer */}
            {isOrderDrawerOpen && (
                <OrderForm
                    title={isEditData ? (isViewOnly ? "View" : "Edit") : "Add"}
                    Position="right"
                    isOpen={isOrderDrawerOpen}
                    editableObj={selectedOrder}
                    worktype="new"
                    isEditData={isEditData}
                    isViewOnly={isViewOnly}
                    approvalToken={approvalToken}
                    approvalStatus={approvalStatus}
                    onRequestEditClick={() => {
                        setRequestEditDoc(selectedOrder);
                        setIsRequestEditModalOpen(true);
                    }}
                    onDrawerClose={() => {
                        setIsOrderDrawerOpen(false);
                        setSelectedOrder(null);
                        setIsEditData(false);
                        setIsViewOnly(false);
                        setApprovalToken(null);
                        setApprovalStatus(null);
                        fetchDashboardData();
                    }}
                />
            )}

            {/* Request Edit Modal */}
            {isRequestEditModalOpen && (
                <RequestEditModal
                    isOpen={isRequestEditModalOpen}
                    toggle={() => setIsRequestEditModalOpen(!isRequestEditModalOpen)}
                    documentType={requestEditDoc?.document_type || "order"}
                    documentId={requestEditDoc?.id || requestEditDoc?.order_number || selectedOrder?.id || ""}
                    documentNumber={requestEditDoc?.order_number || requestEditDoc?.id || selectedOrder?.order_number || ""}
                    onSuccess={() => {
                        setIsRequestEditModalOpen(false);
                        setApprovalStatus("pending");
                        fetchDashboardData();
                    }}
                />
            )}
        </React.Fragment>
    );
};

export default EmployeeDashboard;
